import * as DialogPrimitive from '@radix-ui/react-dialog';
import * as DropdownPrimitive from '@radix-ui/react-dropdown-menu';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import * as TabsPrimitive from '@radix-ui/react-tabs';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cva, type VariantProps } from 'class-variance-authority';
import { Loader2, X } from 'lucide-react';
import type { ComponentProps, HTMLAttributes, ReactNode } from 'react';
import { cn } from '@/lib/utils';

// ─── Badge ─────────────────────────────────────────────────────────────
const badgeVariants = cva('inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-semibold whitespace-nowrap', {
  variants: {
    variant: {
      neutral: 'bg-sunken text-ink-soft',
      matcha: 'bg-matcha-100 text-matcha-800',
      gold: 'bg-gold-100 text-gold-700',
      overdue: 'bg-overdue-bg text-overdue',
      today: 'bg-today-bg text-today',
      tomorrow: 'bg-tomorrow-bg text-tomorrow',
      upcoming: 'bg-upcoming-bg text-upcoming',
      outline: 'border border-line-strong text-ink-soft',
    },
  },
  defaultVariants: { variant: 'neutral' },
});
export function Badge({ className, variant, ...props }: HTMLAttributes<HTMLSpanElement> & VariantProps<typeof badgeVariants>) {
  return <span className={cn(badgeVariants({ variant }), className)} {...props} />;
}

// ─── Card ──────────────────────────────────────────────────────────────
export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('rounded-card border border-line bg-surface shadow-card', className)} {...props} />;
}
export function CardHeader({ title, icon, actions, className, count }: { title: ReactNode; icon?: ReactNode; actions?: ReactNode; className?: string; count?: number }) {
  return (
    <div className={cn('flex items-center justify-between gap-2 border-b border-line px-4 py-3', className)}>
      <h3 className="flex items-center gap-2 text-[15px] font-semibold [&_svg]:size-4 [&_svg]:text-matcha-600">
        {icon}
        {title}
        {count !== undefined && <span className="rounded-full bg-sunken px-1.5 text-xs font-bold text-muted tabular">{count}</span>}
      </h3>
      {actions && <div className="flex items-center gap-1">{actions}</div>}
    </div>
  );
}

// ─── Feedback ──────────────────────────────────────────────────────────
export function Spinner({ className }: { className?: string }) {
  return <Loader2 className={cn('size-5 animate-spin text-matcha-600', className)} aria-label="טוען" />;
}
export function Skeleton({ className }: { className?: string }) {
  return <div className={cn('animate-pulse rounded-md bg-sunken', className)} />;
}
export function EmptyState({ icon, title, description, action, className }: { icon?: ReactNode; title: string; description?: ReactNode; action?: ReactNode; className?: string }) {
  return (
    <div className={cn('flex flex-col items-center justify-center gap-2 px-6 py-10 text-center', className)}>
      {icon && <div className="mb-1 grid size-11 place-items-center rounded-full bg-matcha-50 text-matcha-600 [&_svg]:size-5">{icon}</div>}
      <p className="font-semibold text-ink-soft">{title}</p>
      {description && <p className="max-w-sm text-sm text-muted">{description}</p>}
      {action && <div className="mt-2">{action}</div>}
    </div>
  );
}
export function PageLoader() {
  return (
    <div className="grid min-h-[40vh] place-items-center">
      <Spinner className="size-7" />
    </div>
  );
}

// ─── Dialog ────────────────────────────────────────────────────────────
export const Dialog = DialogPrimitive.Root;
export const DialogTrigger = DialogPrimitive.Trigger;
export const DialogClose = DialogPrimitive.Close;
export function DialogContent({
  title,
  description,
  children,
  className,
  wide,
}: {
  title: ReactNode;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <DialogPrimitive.Portal>
      <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-matcha-950/40 backdrop-blur-[1px] animate-fade-in" />
      <DialogPrimitive.Content
        dir="rtl"
        className={cn(
          'fixed inset-x-0 top-[8vh] z-50 mx-auto max-h-[84vh] w-[calc(100vw-2rem)] overflow-y-auto',
          'rounded-card border border-line bg-surface shadow-pop animate-pop-in focus:outline-none',
          wide ? 'max-w-2xl' : 'max-w-lg',
          className,
        )}
      >
        <div className="sticky top-0 z-10 flex items-start justify-between gap-4 border-b border-line bg-surface px-5 py-4">
          <div>
            <DialogPrimitive.Title className="font-display text-lg font-semibold">{title}</DialogPrimitive.Title>
            {description ? (
              <DialogPrimitive.Description className="mt-0.5 text-sm text-muted">{description}</DialogPrimitive.Description>
            ) : (
              <DialogPrimitive.Description className="sr-only">{typeof title === 'string' ? title : ''}</DialogPrimitive.Description>
            )}
          </div>
          <DialogPrimitive.Close className="rounded-md p-1 text-muted hover:bg-sunken hover:text-ink" aria-label="סגירה">
            <X className="size-5" />
          </DialogPrimitive.Close>
        </div>
        <div className="px-5 py-4">{children}</div>
      </DialogPrimitive.Content>
    </DialogPrimitive.Portal>
  );
}

// ─── Dropdown ──────────────────────────────────────────────────────────
export const DropdownMenu = DropdownPrimitive.Root;
export const DropdownMenuTrigger = DropdownPrimitive.Trigger;
export function DropdownMenuContent({ className, align = 'end', ...props }: ComponentProps<typeof DropdownPrimitive.Content>) {
  return (
    <DropdownPrimitive.Portal>
      <DropdownPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn('z-50 min-w-44 rounded-control border border-line bg-surface p-1 shadow-pop animate-pop-in', className)}
        {...props}
      />
    </DropdownPrimitive.Portal>
  );
}
export function DropdownMenuItem({ className, ...props }: ComponentProps<typeof DropdownPrimitive.Item>) {
  return (
    <DropdownPrimitive.Item
      className={cn(
        'flex cursor-pointer items-center gap-2 rounded-md px-2.5 py-1.5 text-sm text-ink outline-none select-none',
        'data-[highlighted]:bg-matcha-50 data-[highlighted]:text-matcha-900 data-[disabled]:opacity-50 [&_svg]:size-4 [&_svg]:text-muted',
        className,
      )}
      {...props}
    />
  );
}
export const DropdownMenuSeparator = () => <DropdownPrimitive.Separator className="my-1 h-px bg-line" />;
export const DropdownMenuLabel = ({ children }: { children: ReactNode }) => (
  <DropdownPrimitive.Label className="px-2.5 py-1 text-xs font-semibold text-muted">{children}</DropdownPrimitive.Label>
);

// ─── Popover ───────────────────────────────────────────────────────────
export const Popover = PopoverPrimitive.Root;
export const PopoverTrigger = PopoverPrimitive.Trigger;
export const PopoverAnchor = PopoverPrimitive.Anchor;
export function PopoverContent({ className, align = 'end', ...props }: ComponentProps<typeof PopoverPrimitive.Content>) {
  return (
    <PopoverPrimitive.Portal>
      <PopoverPrimitive.Content
        align={align}
        sideOffset={6}
        className={cn('z-50 rounded-card border border-line bg-surface p-3 shadow-pop animate-pop-in focus:outline-none', className)}
        {...props}
      />
    </PopoverPrimitive.Portal>
  );
}

// ─── Tabs ──────────────────────────────────────────────────────────────
export const Tabs = (props: ComponentProps<typeof TabsPrimitive.Root>) => <TabsPrimitive.Root dir="rtl" {...props} />;
export function TabsList({ className, ...props }: ComponentProps<typeof TabsPrimitive.List>) {
  return <TabsPrimitive.List className={cn('flex gap-1 border-b border-line', className)} {...props} />;
}
export function TabsTrigger({ className, ...props }: ComponentProps<typeof TabsPrimitive.Trigger>) {
  return (
    <TabsPrimitive.Trigger
      className={cn(
        '-mb-px flex items-center gap-1.5 border-b-2 border-transparent px-3 py-2 text-sm font-semibold text-muted transition-colors',
        'hover:text-ink data-[state=active]:border-matcha-600 data-[state=active]:text-matcha-800 [&_svg]:size-4',
        className,
      )}
      {...props}
    />
  );
}
export const TabsContent = TabsPrimitive.Content;

// ─── Tooltip ───────────────────────────────────────────────────────────
export const TooltipProvider = TooltipPrimitive.Provider;
export function Tooltip({ content, children }: { content: ReactNode; children: ReactNode }) {
  return (
    <TooltipPrimitive.Root delayDuration={300}>
      <TooltipPrimitive.Trigger asChild>{children}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          sideOffset={4}
          className="z-50 max-w-xs rounded-md bg-matcha-950 px-2 py-1 text-xs text-white shadow-pop animate-fade-in"
        >
          {content}
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
